import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, ShieldCheck, Lock, AlertCircle, Heart, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const sessionKey = (token) => `whisper_view_session_${token}`;

const NAVY = '#16233F';
const GOLD = '#C9A24B';
const PURPLE = '#6D28D9';

export default function WhisperView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { sender_name, title, recipient_email_masked }
  const [whisper, setWhisper] = useState(null);
  const [email, setEmail] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState('');

  useEffect(() => {
    init();
  }, [token]);

  const init = async () => {
    if (!token) {
      setError('This message link is invalid. Please open the link from your email.');
      setLoading(false);
      return;
    }
    // Try an existing verified session first (survives refresh after single-use consume).
    const existing = sessionStorage.getItem(sessionKey(token));
    if (existing) {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/whispers/view-session/${existing}`);
        setWhisper(r.data.whisper);
        setLoading(false);
        return;
      } catch {
        sessionStorage.removeItem(sessionKey(token));
      }
    }
    try {
      const r = await axios.get(`${BACKEND_URL}/api/whispers/view/${token}`);
      setPreview(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'This message link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const unlock = async () => {
    setUnlockError('');
    if (!email.trim() || !email.includes('@')) {
      setUnlockError('Please enter the email address this message was sent to.');
      return;
    }
    setUnlocking(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/whispers/view/${token}/unlock`, { email: email.trim() });
      if (r.data.view_session_token) {
        sessionStorage.setItem(sessionKey(token), r.data.view_session_token);
      }
      setWhisper(r.data.whisper);
      toast.success('Message unlocked 💜');
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) {
        setError(err.response?.data?.detail || 'This message link has already been used or expired.');
        setPreview(null);
      } else {
        setUnlockError(err.response?.data?.detail || 'We could not unlock this message. Please try again.');
      }
    } finally {
      setUnlocking(false);
    }
  };

  // ===== Loading =====
  if (loading) {
    return (
      <Shell>
        <div className="text-center py-10">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
            <Loader2 className="h-10 w-10 mx-auto" style={{ color: PURPLE }} />
          </motion.div>
          <p className="mt-4 font-body text-slate-500">Opening your secure message…</p>
        </div>
      </Shell>
    );
  }

  // ===== Error =====
  if (error) {
    return (
      <Shell>
        <div className="text-center py-6" data-testid="whisper-view-error">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[#1C252E] mb-2">Link Unavailable</h1>
          <p className="font-body text-slate-600 mb-6 max-w-sm mx-auto">{error}</p>
          <Button onClick={() => navigate('/login')} className="rounded-full text-white px-8" style={{ background: NAVY }} data-testid="whisper-view-signin">
            Go to Sign In
          </Button>
        </div>
      </Shell>
    );
  }

  // ===== Unlocked message =====
  if (whisper) {
    return (
      <Shell wide>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="whisper-view-content">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: PURPLE }}>
              <Heart className="h-4 w-4" /> A Whisper from
            </div>
            <div className="flex items-center justify-center gap-3 mt-2">
              {whisper.sender_picture ? (
                <img src={whisper.sender_picture} alt={whisper.sender_name} className="w-12 h-12 rounded-full object-cover ring-2 ring-[#EBF0F5]" />
              ) : null}
              <h1 className="font-display text-3xl font-bold text-[#1C252E]">{whisper.sender_name}</h1>
            </div>
            <div className="h-0.5 w-16 mx-auto my-4 rounded" style={{ background: GOLD }} />
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" style={{ borderLeft: `4px solid ${PURPLE}` }}>
            <div className="text-[11px] tracking-widest text-slate-400 font-body">MESSAGE</div>
            <h2 className="font-display text-xl text-[#1C252E] mt-1 mb-4">{whisper.title}</h2>
            <p className="font-body text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="whisper-view-body">{whisper.content}</p>

            <WhisperMedia whisper={whisper} />
          </div>

          <p className="text-center text-xs text-slate-400 mt-6 font-body">
            This message was delivered privately to you through EasyPassing.life
          </p>
        </motion.div>
      </Shell>
    );
  }

  // ===== Verification step =====
  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="whisper-view-verify">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#F3EEFC' }}>
            <Lock className="h-8 w-8" style={{ color: PURPLE }} />
          </div>
          <div className="text-sm font-semibold" style={{ color: PURPLE }}>💜 A Whisper from</div>
          <h1 className="font-display text-2xl font-bold text-[#1C252E] mt-1">{preview?.sender_name}</h1>
          <div className="h-0.5 w-14 mx-auto my-4 rounded" style={{ background: GOLD }} />
          <p className="font-body text-slate-600 max-w-sm mx-auto">
            For your privacy, please confirm your email address to open this secure message.
          </p>
          {preview?.recipient_email_masked && (
            <p className="font-body text-xs text-slate-400 mt-2">Sent to {preview.recipient_email_masked}</p>
          )}
        </div>

        <div className="space-y-3">
          <label className="text-xs font-medium text-slate-500 font-body">Your email address</label>
          <div className="relative">
            <Mail className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') unlock(); }}
              placeholder="you@example.com"
              className="w-full h-12 pl-10 pr-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-[#6D28D9]"
              data-testid="whisper-view-email"
            />
          </div>
          {unlockError && <p className="text-xs text-red-600" data-testid="whisper-view-unlock-error">{unlockError}</p>}
          <Button
            onClick={unlock}
            disabled={unlocking}
            className="w-full rounded-full text-white py-6 font-semibold"
            style={{ background: PURPLE }}
            data-testid="whisper-view-unlock"
          >
            {unlocking ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Verifying…</>) : (<><ShieldCheck className="h-5 w-5 mr-2" /> Open Message</>)}
          </Button>
          <p className="text-center text-[11px] text-slate-400 font-body leading-relaxed pt-1">
            This link is private and single-use. EasyPassing will never ask you to email your password or financial information.
          </p>
        </div>
      </motion.div>
    </Shell>
  );
}

function WhisperMedia({ whisper }) {
  const { type, media_url, album } = whisper;
  const photos = album?.photos || [];
  const videos = album?.videos || [];
  const audio = album?.audio_recordings || [];

  return (
    <div className="mt-5 space-y-4">
      {type === 'image' && media_url && (
        <img src={media_url} alt="Shared with your whisper" className="w-full rounded-xl" data-testid="whisper-view-image" />
      )}
      {type === 'video' && media_url && (
        <video src={media_url} controls className="w-full rounded-xl" data-testid="whisper-view-video" />
      )}
      {(type === 'audio' || type === 'voice') && media_url && (
        <audio src={media_url} controls className="w-full" data-testid="whisper-view-audio" />
      )}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="whisper-view-album-photos">
          {photos.map((p, i) => (
            <img key={i} src={p} alt={`Memory ${i + 1}`} className="w-full h-28 object-cover rounded-lg" />
          ))}
        </div>
      )}
      {videos.map((v, i) => (
        <video key={i} src={v} controls className="w-full rounded-xl" data-testid={`whisper-view-album-video-${i}`} />
      ))}
      {audio.map((a, i) => (
        <audio key={i} src={a} controls className="w-full" data-testid={`whisper-view-album-audio-${i}`} />
      ))}
    </div>
  );
}

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF7EF] p-4 sm:p-6 font-body">
      <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'}`}>
        <div className="bg-white rounded-3xl shadow-[0_10px_40px_rgba(22,35,63,0.12)] overflow-hidden">
          <div className="text-center py-5" style={{ background: NAVY }}>
            <div className="font-display text-2xl font-bold text-white">
              Easy<span style={{ color: GOLD }}>Passing</span>
            </div>
            <div className="h-0.5 w-14 mx-auto mt-2 rounded" style={{ background: GOLD }} />
          </div>
          <div className="p-6 sm:p-8">{children}</div>
        </div>
      </div>
    </div>
  );
}
